function setMediaTime(media: HTMLMediaElement, currentTime: number) {
    media.currentTime = currentTime;
}

class AudioPlayer extends EventTarget {
    private playingAudio: HTMLAudioElement;
    private playlist: string[];
    private index: number;

    public volume: number;
    public muted: boolean;
    public playbackRate: number;
    public loop: boolean;
    public shuffle: boolean;

    constructor() {
        super();
        this.playlist = [];
        this.playingAudio = new Audio();
        this.index = 0;
        this.volume = 1;
        this.muted = false;
        this.playbackRate = 1;
        this.loop = false;
        this.shuffle = false;
    }

    public toggle(play?: boolean, media: HTMLAudioElement = this.playingAudio) {
        if (!media) {
            return false;
        }

        if (play === undefined) {
            play = media.paused;
        }

        if (media.paused !== play) {
            return false;
        }

        if (play) {
            media.play();
        } else {
            media.pause();
        }

        return true;
    }

    public getPlayingAudio() {
        return this.playingAudio;
    }

    public play = () => {
        return this.toggle(true);
    };

    public pause = () => {
        return this.toggle(false);
    };

    public stop = (media = this.playingAudio) => {
        if (!media) {
            return false;
        }

        if (!media.paused) {
            media.pause();
        }

        setMediaTime(media, 0);

        return true;
    };

    public first = () => {
        if (!this.playlist || this.playlist.length === 0) return false;
        return this.go(-this.index);
    };

    public last = () => {
        if (!this.playlist || this.playlist.length === 0) return false;
        const lastIndex = this.playlist.length - 1;
        const offset = lastIndex - this.index;
        return this.go(offset);
    };

    public next = () => {
        return this.go(1);
    };

    public previous = () => {
        return this.go(-1);
    };

    public random = () => {
        if (!this.playlist || this.playlist.length === 0) return false;
        const randomIndex = Math.floor(Math.random() * this.playlist.length);
        const length = randomIndex - this.index;
        return this.go(length);
    };

    public playAudio = (audio: HTMLAudioElement) => {
        audio.play();
    };

    public campaignAudio = () => {
        if (!this.playlist || this.playlist.length === 0) return;
        this.shuffle ? this.random() : this.first();
    };

    public go = (length: number) => {
        if (!this.playlist || this.playlist.length === 0) return false;
        const newIndex = (this.index + length + this.playlist.length) % this.playlist.length;
        if (newIndex < 0 || newIndex >= this.playlist.length) return false;
        this.index = newIndex;
        const audio = new Audio(this.playlist[this.index]);
        this.subscribeAudio(audio);
        this.playAudio(audio);
        return true;
    };

    private onPlay = (e?: Event) => {
        const audio = e?.target as HTMLAudioElement;

        const previousAudio = this.playingAudio;

        if (previousAudio !== audio) {
            this.stop();
            this.setAudio(audio);
        }

        setTimeout(() => {
            if (this.playingAudio !== audio) {
                return;
            }

            this.dispatchEvent(new CustomEvent('play'));
        }, 0);
    }

    private onPause = (e?: Event) => {
        this.dispatchEvent(new CustomEvent('pause'));
    };

    private onEnded = (e?: Event) => {
        if (e && !e.isTrusted) {
            return;
        }

        this.onPause(e);

        if (
            !this.playlist || this.playlist.length === 0 ||
            !(this.shuffle ? this.random() : this.next())
        ) {
            this.stop();
            this.dispatchEvent(new CustomEvent('stop'));
        }
    };

    public addAudio(url: string): void {
        this.playlist.push(url);
    }

    public subscribeAudio(audio: HTMLAudioElement) {
        audio.addEventListener('play', this.onPlay);
        audio.addEventListener('pause', this.onPause);
        audio.addEventListener('ended', this.onEnded);
    }

    public setAudio(audio: HTMLAudioElement) {
        this.playingAudio = audio;
        this.playingAudio.volume = this.volume;
        this.playingAudio.muted = this.muted;
        this.playingAudio.playbackRate = this.playbackRate;
        this.playingAudio.loop = this.loop;
    }
}

const audioPlayer = new AudioPlayer();
export default audioPlayer;
